'use client'

import { type AxiosError } from 'axios'
import { type SubmitEventHandler, useCallback, useRef, useState } from 'react'
import { type UseFormReturn } from 'react-hook-form'

import { useOneTimePasswordApiRequestMutation } from '../../../login-registration/api/swr/useOneTimePasswordApiRequestMutation'
import {
  type UserLoginResponseError,
  useUserApiLoginMutation,
} from '../../../login-registration/api/swr/useUserApiLoginMutation'
import { LOGIN_OTP_STATE } from '../../../login-registration/context/LoginRegistrationUserLoginStateContext'
import {
  type LoginAfterLoggedIn,
  makeLoginAfterLoggedInForMethod,
} from '../../../login-registration/loginAfterLoggedIn'
import { type LoginPasswordTracking } from '../loginPasswordTracking'

export type PasswordLoginFormData = {
  fcSolution: string
  password: string
}

type SubmitIntent = 'otp' | 'password'

type UserPasswordLoginHookProps = {
  afterLoggedIn?: LoginAfterLoggedIn
  formMethods: UseFormReturn<PasswordLoginFormData>
  setLoginOtpState: (loginOtpState: LOGIN_OTP_STATE) => void
  tracking: LoginPasswordTracking
  username: string
}

export type PasswordLoginSubmissionResult = {
  isLoginMutating: boolean
  isOtpMutating: boolean
  isSubmitting: boolean
  loginError: AxiosError<UserLoginResponseError> | undefined
  onFormSubmit: SubmitEventHandler<HTMLFormElement>
  otpError: AxiosError | undefined
  requestOtp: (fcSolution: string) => Promise<void>
  submitIntent: SubmitIntent | null
}

const usePasswordFormSubmitHandler = (
  handleSubmit: UseFormReturn<PasswordLoginFormData>['handleSubmit'],
  submitPassword: (formData: PasswordLoginFormData) => Promise<void>,
): SubmitEventHandler<HTMLFormElement> =>
  useCallback(
    (event): void => {
      handleSubmit(submitPassword)(event).catch(() => undefined)
    },
    [handleSubmit, submitPassword],
  )

type PasswordLoginMutations = {
  loginMutation: {
    error: AxiosError<UserLoginResponseError> | undefined
    isMutating: boolean
    reset: () => void
    trigger: (
      request: PasswordLoginFormData & { username: string },
    ) => Promise<unknown>
  }
  otpMutation: {
    error: AxiosError | undefined
    isMutating: boolean
    reset: () => void
    trigger: (request: {
      fcSolution: string
      username: string
    }) => Promise<unknown>
  }
}

const usePasswordLoginMutations = ({
  afterLoggedIn,
  finishSubmission,
  resetPassword,
  setLoginOtpState,
  tracking,
}: {
  afterLoggedIn: UserPasswordLoginHookProps['afterLoggedIn']
  finishSubmission: () => void
  resetPassword: () => void
  setLoginOtpState: (loginOtpState: LOGIN_OTP_STATE) => void
  tracking: LoginPasswordTracking
}): PasswordLoginMutations => {
  const loginMutation = useUserApiLoginMutation({
    onError: () => {
      tracking.track('failure')
      resetPassword()
      finishSubmission()
    },
    onSuccess: makeLoginAfterLoggedInForMethod(afterLoggedIn, 'password'),
  })

  const otpMutation = useOneTimePasswordApiRequestMutation({
    onError: () => {
      resetPassword()
      finishSubmission()
    },
    onSuccess: () => {
      resetPassword()
      setLoginOtpState(LOGIN_OTP_STATE.OTP_INPUT)
      finishSubmission()
    },
  })

  return { loginMutation, otpMutation }
}

export const useLoginRegistrationUserPasswordLoginSubmission = ({
  afterLoggedIn,
  formMethods,
  setLoginOtpState,
  tracking,
  username,
}: UserPasswordLoginHookProps): PasswordLoginSubmissionResult => {
  const [submitIntent, setSubmitIntent] = useState<SubmitIntent | null>(null)
  const submitInProgressRef = useRef(false)
  const { handleSubmit, resetField } = formMethods
  const finishSubmission = useCallback((): void => {
    submitInProgressRef.current = false
    setSubmitIntent(null)
  }, [])

  const resetPassword = useCallback((): void => {
    resetField('password')
  }, [resetField])

  const { loginMutation, otpMutation } = usePasswordLoginMutations({
    afterLoggedIn,
    finishSubmission,
    resetPassword,
    setLoginOtpState,
    tracking,
  })

  const resetMutationErrors = useCallback((): void => {
    loginMutation.reset()
    otpMutation.reset()
  }, [loginMutation, otpMutation])

  const isMutationInProgress = useCallback(
    (): boolean =>
      submitInProgressRef.current ||
      loginMutation.isMutating ||
      otpMutation.isMutating,
    [loginMutation.isMutating, otpMutation.isMutating],
  )

  const submitPassword = useCallback(
    async (formData: PasswordLoginFormData): Promise<void> => {
      if (isMutationInProgress()) {
        return
      }

      resetMutationErrors()
      submitInProgressRef.current = true
      setSubmitIntent('password')
      tracking.track('submit')

      try {
        await loginMutation.trigger({ ...formData, username })
      } catch {
        finishSubmission()
      }
    },
    [
      finishSubmission,
      isMutationInProgress,
      loginMutation,
      resetMutationErrors,
      tracking,
      username,
    ],
  )

  const onFormSubmit = usePasswordFormSubmitHandler(
    handleSubmit,
    submitPassword,
  )

  const requestOtp = useCallback(
    async (fcSolution: string): Promise<void> => {
      if (!fcSolution || isMutationInProgress()) {
        return
      }

      resetMutationErrors()
      submitInProgressRef.current = true
      setSubmitIntent('otp')
      tracking.track('switch_to_otp')

      try {
        await otpMutation.trigger({ fcSolution, username })
      } catch {
        finishSubmission()
      }
    },
    [
      finishSubmission,
      isMutationInProgress,
      otpMutation,
      resetMutationErrors,
      tracking,
      username,
    ],
  )

  return {
    isLoginMutating: loginMutation.isMutating,
    isOtpMutating: otpMutation.isMutating,
    isSubmitting:
      submitIntent !== null ||
      formMethods.formState.isSubmitting ||
      loginMutation.isMutating ||
      otpMutation.isMutating,
    loginError: loginMutation.error,
    onFormSubmit,
    otpError: otpMutation.error,
    requestOtp,
    submitIntent,
  }
}
